import { createSelector } from 'reselect';
import { Schedule, Session, ScheduleGroup } from '../models/Schedule';
import { Speaker } from '../models/Speaker';
import { Location } from '../models/Location';
import { AppState } from './state';

const getSchedule = (state: AppState) => {
  return state.data.schedule;
};
const getAllSpeakers = (state: AppState) => state.data.speakers;
const getSessions = (state: AppState, _?: any) => state.data.sessions;
const getFilteredTracks = (state: AppState) => state.data.filteredTracks;
const getFavoriteIds = (state: AppState) => state.data.favorites;
const getSearchText = (state: AppState) => state.data.searchText;
const getSpeakerSearchText = (state: AppState) => state.data.speakerSearchText;

export const getSpeakers = createSelector(
  getAllSpeakers,
  getSpeakerSearchText,
  (speakers, searchText) => {
    // Primero filtrar speakers que tengan nombre válido
    let filteredSpeakers = speakers.filter((speaker: Speaker) =>
      speaker.name && speaker.name.trim() !== ''
    );

    if (searchText) {
      filteredSpeakers = filteredSpeakers.filter((speaker: Speaker) => {
        return (
          speaker.name.toLowerCase().includes(searchText.toLowerCase()) ||
          (speaker.title && speaker.title.toLowerCase().includes(searchText.toLowerCase())) ||
          (speaker.company && speaker.company.toLowerCase().includes(searchText.toLowerCase())) ||
          (speaker.location && speaker.location.toLowerCase().includes(searchText.toLowerCase()))
        );
      });
    }

    // Ordenar alfabéticamente por nombre
    return filteredSpeakers.sort((a: Speaker, b: Speaker) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  }
);

export const getFilteredSchedule = createSelector(
  getSchedule,
  getFilteredTracks,
  (schedule, filteredTracks) => {
    const groups: ScheduleGroup[] = [];
    schedule.groups.forEach((group: ScheduleGroup) => {
      const sessions: Session[] = [];
      group.sessions.forEach((session) => {
        session.tracks.forEach((track) => {
          if (filteredTracks.indexOf(track) > -1) {
            sessions.push(session);
          }
        });
      });
      if (sessions.length) {
        const groupToAdd: ScheduleGroup = {
          time: group.time,
          sessions,
        };
        groups.push(groupToAdd);
      }
    });

    return {
      date: schedule.date,
      groups,
    } as Schedule;
  }
);

export const getSearchedSchedule = createSelector(
  getFilteredSchedule,
  getSearchText,
  (schedule, searchText) => {
    if (!searchText) {
      return schedule;
    }

    const searchTermLower = searchText.toLowerCase().trim();
    const groups: ScheduleGroup[] = [];

    schedule.groups.forEach((group) => {
      const sessions = group.sessions.filter((session) => {
        // Búsqueda en el nombre de la sesión
        const nameMatch = session.name.toLowerCase().includes(searchTermLower);

        // Búsqueda en la descripción
        const descriptionMatch = session.description &&
          session.description.toLowerCase().includes(searchTermLower);

        // Búsqueda en speakers (speakerNames)
        const speakersMatch = session.speakerNames &&
          session.speakerNames.some(speaker =>
            speaker.toLowerCase().includes(searchTermLower)
          );

        // Búsqueda en sala/ubicación (location)
        const locationMatch = session.location &&
          session.location.toLowerCase().includes(searchTermLower);

        // Búsqueda en área temática (tracks)
        const tracksMatch = session.tracks &&
          session.tracks.some(track =>
            track.toLowerCase().includes(searchTermLower)
          );

        return nameMatch || descriptionMatch || speakersMatch || locationMatch || tracksMatch;
      });

      if (sessions.length) {
        const groupToAdd: ScheduleGroup = {
          time: group.time,
          sessions,
        };
        groups.push(groupToAdd);
      }
    });

    return {
      date: schedule.date,
      groups,
    } as Schedule;
  }
);

export const getScheduleList = createSelector(
  getSearchedSchedule,
  (schedule) => schedule
);

export const getGroupedFavorites = createSelector(
  getScheduleList,
  getFavoriteIds,
  (schedule, favoriteIds) => {
    const groups: ScheduleGroup[] = [];
    schedule.groups.forEach((group) => {
      const sessions = group.sessions.filter(
        (s) => favoriteIds.indexOf(s.id) > -1
      );
      if (sessions.length) {
        const groupToAdd: ScheduleGroup = {
          time: group.time,
          sessions,
        };
        groups.push(groupToAdd);
      }
    });
    return {
      date: schedule.date,
      groups,
    } as Schedule;
  }
);

const getIdParam = (_state: AppState, props: any) => {
  return props.match.params['id'];
};

export const getSession = createSelector(getSessions, getIdParam, (sessions, id) => {
  console.log('getSession: Buscando sesión con ID:', id, 'tipo:', typeof id);
  console.log('getSession: Total sesiones disponibles:', sessions.length);

  if (!id || sessions.length === 0) {
    console.log('getSession: ID vacío o no hay sesiones');
    return undefined;
  }

  // Buscar por comparación exacta primero
  let session = sessions.find((s: Session) => s.id === id);

  // Si no se encuentra, intentar comparación con conversión de tipos
  if (!session) {
    session = sessions.find((s: Session) => s.id.toString() === id.toString());
  }

  // Si aún no se encuentra, intentar comparación más flexible
  if (!session) {
    session = sessions.find((s: Session) => {
      const sessionId = String(s.id).trim();
      const searchId = String(id).trim();
      return sessionId === searchId;
    });
  }

  console.log('getSession: Sesión encontrada:', session ? { id: session.id, name: session.name } : 'No encontrada');

  if (!session) {
    console.log('getSession: IDs de sesiones disponibles:', sessions.map((s: Session) => s.id));
  }

  return session;
});


export const getSpeaker = (state: AppState) => {
  const speakers = state.data.speakers;
  return speakers[0]; // Devuelve el primer conferencista como fallback
};

export const getSpeakerSessions = createSelector(
  getSessions,
  (sessions) => {
    const speakerSessions: { [key: string]: Session[] } = {};

    sessions.forEach((session: Session) => {
      session.speakerNames &&
        session.speakerNames
          .filter(name => name && name.trim() !== '')
          .forEach((name) => {
            if (speakerSessions[name]) {
              speakerSessions[name].push(session);
            } else {
              speakerSessions[name] = [session];
            }
          });
    });
    return speakerSessions;
  }
);

export const mapCenter = (state: AppState) => {
  const item = state.data.locations.find(
    (l: Location) => l.id === state.data.mapCenterId
  );
  if (item == null) {
    return {
      id: 1,
      name: 'Map Center',
      lat: 43.071584,
      lng: -89.38012,
    };
  }
  return item;
};
